/**
 * Copyright © 2025-2026 Indisea Software LLC. All rights reserved.
 *
 * Proprietary and confidential. Licensed, not sold. Use is subject to the
 * license agreement between Indisea Software LLC and the licensee.
 * See LICENSE for full terms.
 *
 * SPDX-License-Identifier: LicenseRef-Indisea-Proprietary
 */

import React, { useCallback, useEffect, useState, useRef } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Typography,
    TextField,
    IconButton,
    Tooltip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Autocomplete,
    MenuItem,
    Alert,
    Stack,
    LinearProgress,
    Backdrop,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import apiHeaders from "./apiHeaders";
import { handleSessionExpiredClose } from "./logout";
import * as XLSX from "xlsx/dist/xlsx.full.min.js";
import { saveAs } from "file-saver";
import {
    POSTING_OPTIONS,
    DEFAULT_POSTING_OPTION,
    getPostingOptionLabel,
    resolvePostingOptionValue,
} from "./postingOptions";

// Default Marketplace value used whenever a row has no (or an empty) selection - mirrors the sibling
// QBO connector's marketplace field so both products behave identically for a customer with no
// marketplace assigned (see MarketplaceOption.None on the backend).
const DEFAULT_MARKETPLACE = "None";
const normalizeMarketplace = (value) => value || DEFAULT_MARKETPLACE;

// Reads a response body as JSON, tolerating an empty or non-JSON payload (an error page, a 204)
// by returning an empty list. Module scope keeps it stable across renders so the data-loading
// callbacks below don't have to list it as a dependency.
const safeJson = async (res) => {
    const text = await res.text();
    if (!text) return [];
    try {
        return JSON.parse(text);
    } catch {
        return [];
    }
};

function TaxCodeMapping() {
    const ROW_STATUS = {
        SAVED: "saved",
        MODIFIED: "modified",
        IMPORTED: "imported",
        ERROR: "error",
    };

    const [items, setItems] = useState([]);
    const [taxCodes, setTaxCodes] = useState([]);
    const [marketplaces, setMarketplaces] = useState([DEFAULT_MARKETPLACE]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [filterText, setFilterText] = useState("");
    const [unsavedChanges, setUnsavedChanges] = useState(false);
    const [message, setMessage] = useState({ type: "", text: "" });

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [rowStatuses, setRowStatuses] = useState({});
    const [statusFilter, setStatusFilter] = useState("all");

    const [importing, setImporting] = useState(false);
    const [importProgress, setImportProgress] = useState(0);
    const [importSummary, setImportSummary] = useState(null);
    const [orphanErrors, setOrphanErrors] = useState([]);
    const [errorDetail, setErrorDetail] = useState(null);
    const fileInputRef = useRef(null);

    const tenantId = sessionStorage.getItem("tenantId");

    const fetchData = useCallback(async () => {
        if (!tenantId) {
            setItems([]);
            setTaxCodes([]);
            return;
        }

        setLoading(true);

        try {
            const [itemsRes, taxCodesRes, marketplaceRes] = await Promise.all([
                fetch(`${process.env.REACT_APP_CONNECTOR_API_URL}/api/customer/mapping?tenantId=${tenantId}`, {
                    headers: { ...apiHeaders, Authorization: `${sessionStorage.getItem("token")}` },
                }),
                fetch(`${process.env.REACT_APP_CONNECTOR_API_URL}/api/customer/zamp?tenantId=${tenantId}`, {
                    headers: { ...apiHeaders, Authorization: `${sessionStorage.getItem("token")}` },
                }),
                fetch(`${process.env.REACT_APP_CONNECTOR_API_URL}/api/customer/marketplace`, {
                    headers: { ...apiHeaders, Authorization: `${sessionStorage.getItem("token")}` },
                }),
            ]);

            if (itemsRes.status === 401 || taxCodesRes.status === 401 || marketplaceRes.status === 401) {
                handleSessionExpiredClose();
                setItems([]);
                setTaxCodes([]);
                return;
            }

            if (!itemsRes.ok || !taxCodesRes.ok) {
                const failedRes = !itemsRes.ok ? itemsRes : taxCodesRes;
                const errorBody = await safeJson(failedRes);
                throw new Error(errorBody?.message || "Something went wrong. Please try again later.");
            }

            const [itemsData, taxCodesData] = await Promise.all([safeJson(itemsRes), safeJson(taxCodesRes)]);
            // Marketplace options are non-critical: fall back to a safe default if unavailable,
            // rather than failing the whole page over a dropdown's option list.
            const marketplaceData = marketplaceRes.ok ? await safeJson(marketplaceRes) : [];
            setItems(itemsData || []); // Populate table
            setTaxCodes(taxCodesData || []);
            setMarketplaces(Array.isArray(marketplaceData) && marketplaceData.length ? marketplaceData : [DEFAULT_MARKETPLACE]);
        } catch {
            setItems([]); // Reset table
            setTaxCodes([]);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [tenantId]);


    useEffect(() => {
        fetchData();
    }, [fetchData]);

    /**
     * Applies a partial update to one row: patches the item, flags it Modified and clears any stale
     * banner. Every editable column routes through here so the dirty-tracking behaviour cannot drift
     * between Zamp Entity, External Source and Marketplace.
     */
    const handleFieldChange = (customerId, patch) => {
        setMessage({ type: "", text: "" });
        setItems(prev => prev.map(it => (it.customerId === customerId ? { ...it, ...patch } : it)));
        setRowStatuses(prev => ({
            ...prev,
            [customerId]: { status: ROW_STATUS.MODIFIED, tooltip: "Modified — not yet saved" },
        }));
        setUnsavedChanges(true);
    };

    const handleTaxCodeChange = (customerId, newEntity) =>
        handleFieldChange(customerId, { zampEntity: newEntity });

    // External Source already arrives as a canonical backend value ("Standard"/"PostOnly"/"NoPosting")
    // because the dropdown is built from POSTING_OPTIONS, so no normalization is needed here.
    const handlePostingOptionChange = (customerId, newValue) =>
        handleFieldChange(customerId, { postingOption: newValue });

    // Marketplace is a plain string; clearing the dropdown resets it to the default ("None").
    const handleMarketplaceChange = (customerId, newValue) =>
        handleFieldChange(customerId, { marketplace: normalizeMarketplace(newValue) });

    const handleSave = async () => {
        if (!tenantId) {
            setMessage({ type: "error", text: "Missing tenant information. Please log in again." });
            return;
        }
        setSaving(true);
        setImportSummary(null);
        setOrphanErrors([]);
        setStatusFilter("all");
        try {
            const response = await fetch(`${process.env.REACT_APP_CONNECTOR_API_URL}/api/customer/mapping?tenantId=${tenantId}`, {
                method: "POST",
                headers: { ...apiHeaders, Authorization: `${sessionStorage.getItem("token")}`, "Content-Type": "application/json" },
                body: JSON.stringify(items),
            });
            if (response.status === 401) {
                handleSessionExpiredClose();
                return;
            }
            if (!response.ok) throw new Error("Failed to save mappings");

            setMessage({ type: "success", text: "Customer entity mappings saved successfully!" });
            setUnsavedChanges(false);
            setRowStatuses({});
        } catch (err) {
            setMessage({ type: "error", text: err.message || "Error saving mappings" });
        } finally {
            setSaving(false);
        }
    };

    const handleRefresh = () => {
        setMessage({ type: "", text: "" });
        setRefreshing(true);
        fetchData();

        setRowStatuses({});
        setOrphanErrors([]);
        setImportSummary(null);
        setStatusFilter("all");
        setUnsavedChanges(false);
    };

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleExport = () => {
        // Column order mirrors the on-screen grid, and External Source is written as its display
        // label ("Post only") rather than its backend value so the sheet reads the way the page does -
        // the import side accepts either spelling, so the file still round-trips.
        const exportData = items.map(it => ({
            "Customer Name": it.customerName || "",
            "Zamp Entity": it.zampEntity || "",
            "External Source": getPostingOptionLabel(it.postingOption),
            "Marketplace": normalizeMarketplace(it.marketplace),
        }));
        const ws = XLSX.utils.json_to_sheet(exportData);
        ws["!cols"] = [{ wch: 45 }, { wch: 30 }, { wch: 20 }, { wch: 20 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Customer Entity Mappings");
        const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const blob = new Blob([buf], { type: "application/octet-stream" });
        saveAs(blob, `Customer_Entity_Mappings_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const handleImportClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
            fileInputRef.current.click();
        }
    };

    const handleImportFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setImporting(true);
        setImportProgress(0);
        setMessage({ type: "", text: "" });
        setImportSummary(null);
        setRowStatuses({});
        setOrphanErrors([]);
        setStatusFilter("all");

        try {
            const data = await file.arrayBuffer();
            const wb = XLSX.read(data);
            const ws = wb.Sheets[wb.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });

            if (!rows.length) {
                setMessage({ type: "error", text: "The uploaded file contains no data rows." });
                setImporting(false);
                return;
            }

            const requiredCols = ["Customer Name", "Zamp Entity"];
            const headerKeys = Object.keys(rows[0]);
            const missingCols = requiredCols.filter(c => !headerKeys.includes(c));
            if (missingCols.length > 0) {
                setMessage({ type: "error", text: `Missing required columns: ${missingCols.join(", ")}. Expected: ${requiredCols.join(", ")}` });
                setImporting(false);
                return;
            }

            const itemMap = new Map(items.map(it => [it.customerName.toLowerCase(), it]));
            const validZampEntities = new Set(taxCodes.map(tc => tc.code));
            // "External Source" and "Marketplace" are optional columns - files exported before those
            // features existed don't have them, and re-importing such a file must still work, simply
            // leaving each row's current value untouched (see the blank-value handling below).
            const headerKeySet = new Set(headerKeys);
            const hasExternalSourceColumn = headerKeySet.has("External Source");
            const hasMarketplaceColumn = headerKeySet.has("Marketplace");
            const validMarketplaces = new Set(marketplaces);

            const errors = [];
            const newOrphanErrors = [];
            let successCount = 0;
            const updatedItems = [...items];
            const newStatuses = {};

            for (let i = 0; i < rows.length; i++) {
                const row = rows[i];
                const rowNum = i + 2;
                const name = String(row["Customer Name"] || "").trim();
                const zampEntity = String(row["Zamp Entity"] || "").trim();
                const marketplace = hasMarketplaceColumn ? String(row["Marketplace"] || "").trim() : "";
                const externalSourceRaw = hasExternalSourceColumn ? String(row["External Source"] || "").trim() : "";

                setImportProgress(Math.round(((i + 1) / rows.length) * 100));

                if (!name) {
                    const err = { row: rowNum, customerName: name || "(empty)", zampEntity, error: "Customer Name is empty" };
                    errors.push(err);
                    newOrphanErrors.push({ customerId: `orphan-${rowNum}`, customerName: name || "(empty)", zampEntity, _orphan: true, _error: err.error });
                    continue;
                }

                const existingItem = itemMap.get(name.toLowerCase());
                if (!existingItem) {
                    const err = { row: rowNum, customerName: name, zampEntity, error: "Customer not found in the system" };
                    errors.push(err);
                    newOrphanErrors.push({ customerId: `orphan-${rowNum}`, customerName: name, zampEntity, _orphan: true, _error: err.error });
                    continue;
                }

                if (zampEntity && !validZampEntities.has(zampEntity)) {
                    newStatuses[existingItem.customerId] = {
                        status: ROW_STATUS.ERROR,
                        tooltip: `Import error: Invalid Zamp Entity "${zampEntity}"`,
                        error: `Invalid Zamp Entity: "${zampEntity}"`,
                        rowData: { customerName: name, zampEntity },
                    };
                    errors.push({ row: rowNum, customerName: name, zampEntity, error: newStatuses[existingItem.customerId].error });
                    continue;
                }

                // Accepts the display label or the backend value; anything else is a hard error rather
                // than a silent fallback, so a typo can never quietly change a customer's posting
                // behaviour. A blank cell resolves to undefined and leaves the row's value untouched.
                const importedPostingOption = resolvePostingOptionValue(externalSourceRaw);
                if (externalSourceRaw && !importedPostingOption) {
                    newStatuses[existingItem.customerId] = {
                        status: ROW_STATUS.ERROR,
                        tooltip: `Import error: Invalid External Source "${externalSourceRaw}"`,
                        error: `Invalid External Source: "${externalSourceRaw}"`,
                        rowData: { customerName: name, zampEntity },
                    };
                    errors.push({ row: rowNum, customerName: name, zampEntity, error: newStatuses[existingItem.customerId].error });
                    continue;
                }

                if (marketplace && !validMarketplaces.has(marketplace)) {
                    newStatuses[existingItem.customerId] = {
                        status: ROW_STATUS.ERROR,
                        tooltip: `Import error: Invalid Marketplace "${marketplace}"`,
                        error: `Invalid Marketplace: "${marketplace}"`,
                        rowData: { customerName: name, zampEntity },
                    };
                    errors.push({ row: rowNum, customerName: name, zampEntity, error: newStatuses[existingItem.customerId].error });
                    continue;
                }

                const idx = updatedItems.findIndex(it => it.customerId === existingItem.customerId);
                if (idx !== -1) {
                    const previousZampEntity = updatedItems[idx].zampEntity || "";
                    const previousMarketplace = normalizeMarketplace(updatedItems[idx].marketplace);
                    const previousPostingOption = updatedItems[idx].postingOption || DEFAULT_POSTING_OPTION;
                    const nextMarketplace = marketplace || previousMarketplace;
                    const nextPostingOption = importedPostingOption || previousPostingOption;
                    // A row whose imported values all match what is already on screen stays "saved" -
                    // re-importing an unchanged export must not mark the whole grid dirty.
                    const isRowChanged = zampEntity !== previousZampEntity
                        || nextMarketplace !== previousMarketplace
                        || nextPostingOption !== previousPostingOption;
                    if (isRowChanged) {
                        updatedItems[idx] = {
                            ...updatedItems[idx],
                            zampEntity,
                            marketplace: nextMarketplace,
                            postingOption: nextPostingOption,
                        };
                        newStatuses[existingItem.customerId] = { status: ROW_STATUS.IMPORTED, tooltip: "Updated via import — not yet saved" };
                        successCount++;
                    }
                }

                if ((i + 1) % 100 === 0) {
                    await new Promise(r => setTimeout(r, 0));
                }
            }

            setItems(updatedItems);
            setRowStatuses(newStatuses);
            setOrphanErrors(newOrphanErrors);
            if (successCount > 0) {
                setUnsavedChanges(true);
            }

            setImportProgress(100);
            setImportSummary({
                total: rows.length,
                success: successCount,
                failed: errors.length,
                errors,
            });

            if (errors.length === 0) {
                setMessage({ type: "success", text: `Import completed. ${successCount} mappings updated.` });
            } else if (successCount > 0) {
                setMessage({ type: "warning", text: `Import completed with warnings. ${successCount} updated, ${errors.length} skipped.` });
            } else {
                setMessage({ type: "error", text: `Import failed. All ${errors.length} rows had errors.` });
            }
        } catch (err) {
            setMessage({ type: "error", text: `Failed to read file: ${err.message}` });
        } finally {
            setImporting(false);
        }
    };

    const getRowStatusIcon = (item) => {
        const info = rowStatuses[item.customerId];
        if (!info || info.status === ROW_STATUS.SAVED) {
            return (
                <Tooltip title="Saved" placement="left">
                    <CloudDoneOutlinedIcon sx={{ fontSize: 16, color: "grey.400" }} />
                </Tooltip>
            );
        }
        if (info.status === ROW_STATUS.MODIFIED) {
            return (
                <Tooltip title={info.tooltip} placement="left">
                    <EditIcon sx={{ fontSize: 16, color: "warning.main" }} />
                </Tooltip>
            );
        }
        if (info.status === ROW_STATUS.IMPORTED) {
            return (
                <Tooltip title={info.tooltip} placement="left">
                    <CheckCircleIcon sx={{ fontSize: 16, color: "success.main" }} />
                </Tooltip>
            );
        }
        if (info.status === ROW_STATUS.ERROR) {
            return (
                <Tooltip title={info.tooltip} placement="left">
                    <ErrorOutlineIcon
                        sx={{ fontSize: 16, color: "error.main", cursor: "pointer" }}
                        onClick={() => setErrorDetail(info)}
                    />
                </Tooltip>
            );
        }
        return null;
    };

    const filteredItems = (() => {
        const search = filterText.toLowerCase();
        const base = items.filter(it => {
            // External Source is matched on its display label ("Post only"), which is what the user
            // sees in the column and therefore what they type into the filter.
            const matchesText = it.customerName.toLowerCase().includes(search) ||
                               (it.zampEntity || "").toLowerCase().includes(search) ||
                               getPostingOptionLabel(it.postingOption).toLowerCase().includes(search) ||
                               normalizeMarketplace(it.marketplace).toLowerCase().includes(search);
            if (!matchesText) return false;
            if (statusFilter === "all") return true;
            const s = rowStatuses[it.customerId]?.status || ROW_STATUS.SAVED;
            return s === statusFilter;
        });
        if (statusFilter === ROW_STATUS.ERROR && orphanErrors.length > 0) {
            const filtered = orphanErrors.filter(o =>
                o.customerName.toLowerCase().includes(search) ||
                (o.zampEntity || "").toLowerCase().includes(search)
            );
            return [...base, ...filtered];
        }
        return base;
    })();

    const hasActiveStatuses = Object.keys(rowStatuses).length > 0;
    const statusCounts = (() => {
        if (!hasActiveStatuses) return {};
        const counts = { [ROW_STATUS.SAVED]: 0, [ROW_STATUS.MODIFIED]: 0, [ROW_STATUS.IMPORTED]: 0, [ROW_STATUS.ERROR]: 0 };
        items.forEach(it => {
            const s = rowStatuses[it.customerId]?.status || ROW_STATUS.SAVED;
            counts[s]++;
        });
        return counts;
    })();

    if (loading) return (
        <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "calc(100vh - 64px)" }}>
            <CircularProgress size={48} thickness={4} />
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                Loading mappings...
            </Typography>
        </Box>
    );

    return (
        <Box display="flex" flexDirection="column" gap={2} p={2}>
            {/* Page Heading */}
            <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold", textAlign: "center" }}>
                Customer Entity Mapping Utility
            </Typography>
           
            {/* Table with Filter & Refresh */}
            <Paper elevation={6} sx={{ boxShadow: 4, borderRadius: 2, p: 4, maxWidth: 1050, width: "100%", mx: "auto", mt: 1 }}>
                <Stack spacing={2} direction="column" sx={{ width: "100%", mt: 2 }}>
                    {/* Filter, Import, Export & Refresh */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 2 }}>
                        <TextField
                            size="small"
                            label="Filter by customer name, zamp entity, external source or marketplace"
                            placeholder="Start typing to filter by customer name, zamp entity, external source or marketplace"
                            value={filterText}
                            onChange={e => {
                                setFilterText(e.target.value);
                                setPage(0);
                                if (message.text) setMessage({ type: "", text: "" }); // Clear messages
                            }}
                            sx={{ flexBasis: "50%" }}
                        />
                        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                            <input
                                type="file"
                                accept=".xlsx,.xls"
                                ref={fileInputRef}
                                onChange={handleImportFile}
                                style={{ display: "none" }}
                            />
                            <Tooltip title="Import mappings from Excel file">
                                <span>
                                    <Button variant="outlined" size="small" startIcon={<FileUploadIcon />} onClick={handleImportClick} disabled={importing || items.length === 0}>
                                        Import
                                    </Button>
                                </span>
                            </Tooltip>
                            <Tooltip title="Export mappings to Excel file">
                                <span>
                                    <Button variant="outlined" size="small" startIcon={<FileDownloadIcon />} onClick={handleExport} disabled={items.length === 0}>
                                        Export
                                    </Button>
                                </span>
                            </Tooltip>
                            <Tooltip title="Refresh Xero customers">
                                <IconButton
                                    type="button"
                                    onClick={handleRefresh}
                                    disabled={refreshing}
                                    sx={{
                                        color: refreshing ? "grey.500" : "primary.main",
                                        borderColor: refreshing ? "grey.300" : "primary.main",
                                        "&:hover": { backgroundColor: "primary.light", color: "white" },
                                        transition: "all 0.3s ease",
                                    }}
                                >
                                    <RefreshIcon sx={{ fontSize: 24, animation: refreshing ? "spin 1s linear infinite" : "none" }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>

                    {/* Import Progress */}
                    {importing && (
                        <Box sx={{ width: "100%" }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                <Typography variant="body2" color="text.secondary">Importing...</Typography>
                                <Typography variant="body2" color="text.secondary">{importProgress}%</Typography>
                            </Box>
                            <LinearProgress variant="determinate" value={importProgress} sx={{ height: 8, borderRadius: 4 }} />
                        </Box>
                    )}

                    {/* Import Summary */}
                    {importSummary && !importing && (
                        <Alert
                            severity={importSummary.failed === 0 ? "success" : importSummary.success > 0 ? "warning" : "error"}
                            onClose={() => { setImportSummary(null); setOrphanErrors([]); setRowStatuses(prev => { const next = {}; Object.entries(prev).forEach(([k, v]) => { if (v.status !== ROW_STATUS.ERROR) next[k] = v; }); return next; }); setStatusFilter("all"); }}
                            sx={{ alignItems: "flex-start" }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
                                {importSummary.failed === 0 ? "Import completed" : "Import completed with warnings"}
                            </Typography>
                            <Typography variant="body2">
                                {importSummary.success} updated &nbsp;&bull;&nbsp; {importSummary.failed} skipped &nbsp;&bull;&nbsp; {importSummary.total} total
                            </Typography>
                            {importSummary.failed > 0 && (
                                <Button size="small" variant="text" sx={{ mt: 0.5, p: 0, minWidth: 0, textTransform: "none" }}
                                    onClick={() => setImportSummary({ ...importSummary, showErrors: true })}>
                                    View {importSummary.failed} error{importSummary.failed > 1 ? "s" : ""}
                                </Button>
                            )}
                        </Alert>
                    )}

                    {/* Status Filter Chips */}
                    {hasActiveStatuses && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Status:</Typography>
                            <Chip
                                label={`All (${items.length})`}
                                size="small"
                                variant={statusFilter === "all" ? "filled" : "outlined"}
                                onClick={() => { setStatusFilter("all"); setPage(0); }}
                            />
                            {(statusCounts[ROW_STATUS.IMPORTED] || 0) > 0 && (
                                <Chip
                                    icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                                    label={statusCounts[ROW_STATUS.IMPORTED]}
                                    size="small"
                                    variant={statusFilter === ROW_STATUS.IMPORTED ? "filled" : "outlined"}
                                    color="success"
                                    onClick={() => { setStatusFilter(ROW_STATUS.IMPORTED); setPage(0); }}
                                />
                            )}
                            {(statusCounts[ROW_STATUS.MODIFIED] || 0) > 0 && (
                                <Chip
                                    icon={<EditIcon sx={{ fontSize: 14 }} />}
                                    label={statusCounts[ROW_STATUS.MODIFIED]}
                                    size="small"
                                    variant={statusFilter === ROW_STATUS.MODIFIED ? "filled" : "outlined"}
                                    color="warning"
                                    onClick={() => { setStatusFilter(ROW_STATUS.MODIFIED); setPage(0); }}
                                />
                            )}
                            {((statusCounts[ROW_STATUS.ERROR] || 0) > 0 || orphanErrors.length > 0) && (
                                <Chip
                                    icon={<ErrorOutlineIcon sx={{ fontSize: 14 }} />}
                                    label={(statusCounts[ROW_STATUS.ERROR] || 0) + orphanErrors.length}
                                    size="small"
                                    variant={statusFilter === ROW_STATUS.ERROR ? "filled" : "outlined"}
                                    color="error"
                                    onClick={() => { setStatusFilter(ROW_STATUS.ERROR); setPage(0); }}
                                />
                            )}
                        </Box>
                    )}

                    {/* Table */}
                    <TableContainer sx={{ border: "1px solid rgba(224, 224, 224, 1)", borderRadius: 1 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ height: 50, backgroundColor: "grey.400" }}>
                                    <TableCell sx={{ fontWeight: "bold", fontSize: 16, width: "28%", borderRight: "1px solid rgba(224, 224, 224, 1)" }}>Customer Name</TableCell>
                                    <TableCell sx={{ fontWeight: "bold", fontSize: 16, width: "26%", borderRight: "1px solid rgba(224, 224, 224, 1)" }}>Zamp Entity</TableCell>
                                    <TableCell sx={{ fontWeight: "bold", fontSize: 16, width: "20%", borderRight: "1px solid rgba(224, 224, 224, 1)" }}>External Source</TableCell>
                                    <TableCell sx={{ fontWeight: "bold", fontSize: 16, width: "26%" }}>Marketplace</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item, index) => {
                                    const isOrphan = !!item._orphan;
                                    const info = isOrphan ? null : rowStatuses[item.customerId];
                                    const s = isOrphan ? ROW_STATUS.ERROR : info?.status;
                                    const rowBg = s === ROW_STATUS.IMPORTED
                                        ? (index % 2 === 0 ? "#f6faf3" : "#eef5ea")
                                        : s === ROW_STATUS.ERROR
                                            ? (index % 2 === 0 ? "#fef7f7" : "#fdf0f0")
                                            : s === ROW_STATUS.MODIFIED
                                                ? (index % 2 === 0 ? "#fffbf0" : "#fff7e6")
                                                : (index % 2 === 0 ? "white" : "grey.100");

                                    return (
                                        <TableRow key={item.customerId}
                                            sx={{
                                                backgroundColor: rowBg,
                                            }}>
                                        <TableCell sx={{
                                            borderRight: "1px solid rgba(224, 224, 224, 1)",
                                            maxWidth: 300,              // constrain width (adjust to match your column width)
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        }}>
                                            <Tooltip
                                                title={item.customerName}
                                                placement="bottom"
                                                slotProps={{
                                                    popper: {
                                                        modifiers: [
                                                            { name: "offset", options: { offset: [0, 8] } } // small gap below cell
                                                        ]
                                                    },
                                                    tooltip: {
                                                        sx: {
                                                            color: "white",
                                                            fontSize: "0.8rem",
                                                            borderRadius: 1,
                                                            px: 1.5,
                                                            py: 0.5,
                                                        }
                                                    }
                                                }}
                                            >
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        display: "inline-block",
                                                        maxWidth: "100%",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {item.customerName}
                                                </Box>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ borderRight: "1px solid rgba(224, 224, 224, 1)" }}>
                                            {isOrphan ? (
                                                <Typography variant="body2" color="text.disabled" sx={{ fontSize: 13, fontStyle: "italic", pl: 1 }}>
                                                    {item.zampEntity || "-"}
                                                </Typography>
                                            ) : (
                                                <Autocomplete
                                                    options={taxCodes}
                                                    getOptionLabel={(option) => (typeof option === "string" ? option : option.code)}
                                                    renderOption={(props, option) => <li {...props}>{option.code}</li>}
                                                    value={taxCodes.find(tc => tc.code === item.zampEntity) || item.zampEntity || ""}
                                                    onChange={(e, newValue) => {
                                                        handleTaxCodeChange(item.customerId, newValue ? (typeof newValue === "string" ? newValue : newValue.code) : "");
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField {...params} size="small" placeholder="Start typing to select zamp entity" />
                                                    )}
                                                    fullWidth
                                                />
                                            )}
                                        </TableCell>
                                        {/* External Source — posting mode; options come from the shared POSTING_OPTIONS list */}
                                        <TableCell sx={{ borderRight: "1px solid rgba(224, 224, 224, 1)" }}>
                                            {isOrphan ? (
                                                <Typography variant="body2" color="text.disabled" sx={{ fontSize: 13, fontStyle: "italic", pl: 1 }}>
                                                    -
                                                </Typography>
                                            ) : (
                                                <TextField
                                                    select
                                                    size="small"
                                                    fullWidth
                                                    value={item.postingOption || DEFAULT_POSTING_OPTION}
                                                    onChange={(e) => handlePostingOptionChange(item.customerId, e.target.value)}
                                                    slotProps={{ select: { inputProps: { "aria-label": "External Source" } } }}
                                                >
                                                    {POSTING_OPTIONS.map(option => (
                                                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                                    ))}
                                                </TextField>
                                            )}
                                        </TableCell>
                                        {/* Marketplace — string dropdown with search; clearing resets to "None" */}
                                        <TableCell>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                                <Box sx={{ flex: 1 }}>
                                                    {isOrphan ? (
                                                        <Typography variant="body2" color="text.disabled" sx={{ fontSize: 13, fontStyle: "italic", pl: 1 }}>
                                                            -
                                                        </Typography>
                                                    ) : (() => {
                                                        const currentMarketplace = normalizeMarketplace(item.marketplace);
                                                        const isUnknownMarketplace = currentMarketplace !== DEFAULT_MARKETPLACE && !marketplaces.includes(currentMarketplace);
                                                        return (
                                                            <Autocomplete
                                                                options={marketplaces}
                                                                value={currentMarketplace}
                                                                onChange={(e, newValue) => handleMarketplaceChange(item.customerId, newValue)}
                                                                isOptionEqualToValue={(option, value) => option === value}
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        size="small"
                                                                        placeholder="Start typing to select marketplace"
                                                                        error={isUnknownMarketplace}
                                                                        helperText={isUnknownMarketplace ? `"${currentMarketplace}" is not an available marketplace` : undefined}
                                                                    />
                                                                )}
                                                                fullWidth
                                                            />
                                                        );
                                                    })()}
                                                </Box>
                                                <Box sx={{ flexShrink: 0, width: 20, display: "flex", justifyContent: "center" }}>
                                                    {isOrphan ? (
                                                        <Tooltip title={item._error} placement="left">
                                                            <ErrorOutlineIcon
                                                                sx={{ fontSize: 14, color: "error.main", cursor: "pointer" }}
                                                                onClick={() => setErrorDetail({
                                                                    error: item._error,
                                                                    rowData: { customerName: item.customerName, zampEntity: item.zampEntity },
                                                                })}
                                                            />
                                                        </Tooltip>
                                                    ) : getRowStatusIcon(item)}
                                                </Box>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        component="div"
                        count={filteredItems.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[5, 10, 25]}
                    />

                    <Stack spacing={1} sx={{ mt: 2, width: "100%", maxWidth: 800 }}>
                        {/* Error message */}
                        {message.type === "error" && (
                            <Typography color="error" sx={{ fontSize: 14 }}>
                                {message.text}
                            </Typography>
                        )}

                        {/* Success message */}
                        {message.type === "success" && (
                            <Typography color="success.main" sx={{ fontSize: 14 }}>
                                {message.text}
                            </Typography>
                        )}

                        {/* Warning message - a partially successful import sets this type, which had
                            no branch here and so was silently dropped. */}
                        {message.type === "warning" && (
                            <Typography color="warning.main" sx={{ fontSize: 14 }}>
                                {message.text}
                            </Typography>
                        )}

                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSave}
                            disabled={saving || !unsavedChanges}
                            sx={{ width: 250 }}
                        >
                            {saving ? "Saving..." : "Save"}
                        </Button>
                    </Stack>


                </Stack>
            </Paper>

            <Dialog open={!!importSummary?.showErrors} onClose={() => setImportSummary(prev => prev ? { ...prev, showErrors: false } : null)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: "bold" }}>Import Errors ({importSummary?.failed || 0} rows skipped)</DialogTitle>
                <DialogContent dividers>
                    <TableContainer sx={{ maxHeight: 400 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: "bold", width: 80 }}>Row</TableCell>
                                    <TableCell sx={{ fontWeight: "bold", width: 250 }}>Customer Name</TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>Error</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {importSummary?.errors?.map((err, i) => (
                                    <TableRow key={i} sx={{ backgroundColor: i % 2 === 0 ? "white" : "grey.50" }}>
                                        <TableCell>{err.row}</TableCell>
                                        <TableCell sx={{ fontSize: 13 }}>{err.customerName}</TableCell>
                                        <TableCell sx={{ color: "error.main", fontSize: 13 }}>{err.error}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setImportSummary(prev => prev ? { ...prev, showErrors: false } : null)}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!errorDetail} onClose={() => setErrorDetail(null)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: "bold", color: "error.main" }}>Error Details</DialogTitle>
                <DialogContent dividers>
                    {errorDetail?.rowData && (
                        <Stack spacing={2}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Customer Name</Typography>
                                <Typography variant="body1">{errorDetail.rowData.customerName || "-"}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Zamp Entity</Typography>
                                <Typography variant="body1" sx={{ fontFamily: "monospace" }}>{errorDetail.rowData.zampEntity || "-"}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Error</Typography>
                                <Typography variant="body1" color="error.main">{errorDetail.error}</Typography>
                            </Box>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setErrorDetail(null)}>Close</Button>
                </DialogActions>
            </Dialog>

            <Backdrop open={saving} sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.modal + 1, flexDirection: "column", gap: 2 }}>
                <CircularProgress size={48} thickness={4} color="inherit" />
                <Typography variant="body1">Saving mappings...</Typography>
            </Backdrop>
        </Box>
    );
}

export default TaxCodeMapping;
